import { useState, useEffect, useMemo } from 'react';
import { Modal, Stack, Text, TextInput, Group, Button, Code } from '@mantine/core';
import { ExplorerModalAction } from './types';
import {
  isValidItemName,
  computeDuplicateCandidate,
  getParentPath,
} from './explorerUtils';

export interface ItemActionModalProps {
  action: ExplorerModalAction | null;
  onClose: () => void;
  onConfirm: (action: ExplorerModalAction, newName: string) => Promise<void>;
  isLoading: boolean;
  allFolders: string[];
  regularFiles: string[];
}

export function ItemActionModal({
  action,
  onClose,
  onConfirm,
  isLoading,
  allFolders,
  regularFiles,
}: ItemActionModalProps) {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (!action) {
      setInputValue('');
      return;
    }

    if (action.type === 'createFile' || action.type === 'createFolder') {
      setInputValue('');
    } else if (action.type === 'rename') {
      const name = action.currentPath.split('/').pop() || '';
      setInputValue(name);
    } else if (action.type === 'duplicate') {
      const name = action.currentPath.split('/').pop() || '';
      const parent = getParentPath(action.currentPath);
      const prefix = parent ? `${parent}/` : '';

      const siblings = (action.itemType === 'folder' ? allFolders : regularFiles)
        .filter((p) => {
          if (parent) {
            return p.startsWith(prefix) && !p.slice(prefix.length).includes('/');
          }
          return !p.includes('/');
        })
        .map((p) => (parent ? p.slice(prefix.length) : p));

      const candidate = computeDuplicateCandidate(name, siblings);
      setInputValue(candidate);
    }
  }, [action, allFolders, regularFiles]);

  const trimmed = inputValue.trim();

  const isInvalidFormat = useMemo(() => {
    if (!trimmed) return false;
    return !isValidItemName(trimmed);
  }, [trimmed]);

  const isDuplicate = useMemo(() => {
    if (!action || !trimmed) return false;

    if (action.type === 'createFile') {
      const target = action.targetFolder ? `${action.targetFolder}/${trimmed}` : trimmed;
      return regularFiles.some((f) => f.toLowerCase() === target.toLowerCase());
    }

    if (action.type === 'createFolder') {
      const target = action.targetFolder ? `${action.targetFolder}/${trimmed}` : trimmed;
      return allFolders.some((f) => f.toLowerCase() === target.toLowerCase());
    }

    if (action.type === 'rename') {
      const parent = getParentPath(action.currentPath);
      const target = parent ? `${parent}/${trimmed}` : trimmed;
      const list = action.itemType === 'folder' ? allFolders : regularFiles;
      return list.some((f) => f.toLowerCase() === target.toLowerCase());
    }

    if (action.type === 'duplicate') {
      const parent = getParentPath(action.currentPath);
      const target = parent ? `${parent}/${trimmed}` : trimmed;
      const list = action.itemType === 'folder' ? allFolders : regularFiles;
      return list.some((f) => f.toLowerCase() === target.toLowerCase());
    }

    return false;
  }, [action, trimmed, regularFiles, allFolders]);

  const isSameName = useMemo(() => {
    if (!action || action.type !== 'rename') return false;
    const currentName = action.currentPath.split('/').pop() || '';
    return trimmed.toLowerCase() === currentName.toLowerCase();
  }, [action, trimmed]);

  const isValid = trimmed.length > 0 && !isInvalidFormat && !isDuplicate && !isSameName;

  const handleSubmit = async () => {
    if (!action || !isValid || isLoading) return;
    await onConfirm(action, trimmed);
  };

  if (!action) return null;

  const isFolder =
    action.type === 'createFolder' ||
    ((action.type === 'rename' || action.type === 'duplicate') && action.itemType === 'folder');

  const title = (() => {
    switch (action.type) {
      case 'createFile':
        return '建立新文字檔案';
      case 'createFolder':
        return '建立新資料夾';
      case 'rename':
        return isFolder ? '重新命名資料夾' : '重新命名檔案';
      case 'duplicate':
        return isFolder ? '建立資料夾副本' : '建立檔案副本';
    }
  })();

  const label = (() => {
    switch (action.type) {
      case 'createFile':
        return '檔案名稱 (含副檔名)';
      case 'createFolder':
        return '資料夾名稱';
      case 'rename':
        return `新${isFolder ? '資料夾' : '檔案'}名稱`;
      case 'duplicate':
        return `副本${isFolder ? '資料夾' : '檔案'}名稱`;
    }
  })();

  const description = (() => {
    switch (action.type) {
      case 'createFile':
        return '支援 .json, .html, .css, .js, .ts, .svg, .xml, .md, .txt 等格式';
      case 'createFolder':
        return '支援中英文、數字、底線或破折號';
      case 'duplicate':
        return `將於同一目錄下建立 ${action.currentPath} 的完整複本`;
      default:
        return undefined;
    }
  })();

  const confirmText = (() => {
    switch (action.type) {
      case 'createFile':
        return '建立檔案';
      case 'createFolder':
        return '建立資料夾';
      case 'rename':
        return '確認改名';
      case 'duplicate':
        return '建立副本';
    }
  })();

  const pathContext = (() => {
    if (action.type === 'createFile' || action.type === 'createFolder') {
      return (
        <Text size="xs" c="dimmed">
          目標目錄: <Code>{action.targetFolder ? `${action.targetFolder}/` : '根目錄 /'}</Code>
        </Text>
      );
    }
    return (
      <Text size="xs" c="dimmed">
        原路徑: <Code>{action.currentPath}</Code>
      </Text>
    );
  })();

  const errorMessage = (() => {
    if (isInvalidFormat) {
      return '名稱不能包含 / \\ : * ? " < > | 或路徑穿越符號 (..)';
    }
    if (isSameName) {
      return '新名稱不能與目前名稱相同';
    }
    if (isDuplicate) {
      return `此名稱在同一目錄下已存在`;
    }
    return undefined;
  })();

  return (
    <Modal opened={Boolean(action)} onClose={onClose} title={title} centered size="sm">
      <Stack gap="md">
        {pathContext}

        <TextInput
          label={label}
          description={description}
          value={inputValue}
          onChange={(e) => setInputValue(e.currentTarget.value)}
          error={errorMessage}
          withAsterisk
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && isValid) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} size="xs" disabled={isLoading}>
            取消
          </Button>
          <Button onClick={handleSubmit} loading={isLoading} disabled={!isValid} size="xs">
            {confirmText}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
